"use client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocale } from "next-intl";
import { useInView } from "react-intersection-observer";
import type { EmblaOptionsType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import ProductCard from "@/components/ProductCard";
import { ChevronLeft, ChevronRight, Circle, Clock } from "@/components/icons";
import { Button } from "@/components/ui/button";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
  FormatSubType,
  FormatType,
} from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { ElementClicked } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementClicked";
import { cn } from "@/lib/utils";
import { makeSafeAttributeString } from "@/utils/functions/getIdLabel";
import { TagularVariant } from "@/utils/tagular/dataTranslationMatrices";
import type { RecentlyViewedProductCardListClientProps } from "./types";
import { hyphenateForTagular } from "@/utils/tagular/helpers";
import { convertToRecentlyViewedCard } from "@/utils/convertToRecentlyViewedCard";
import { useDotButton } from "@/components/Carousel/CarouselDotButton";
import { usePrevNextButtons } from "@/components/Carousel/CarouselArrowButtons";
import { formatViewedLabel } from "./utils/formatViewedLabel";
import {
  buildRecentlyViewedMergedWebContext,
  buildRecentlyViewedProductCustomDimensions,
} from "./utils/buildRecentlyViewedTagular";
import { useCarouselMessages } from "@/i18n/hooks/useCarouselMessages";
import useTranslations from "@/i18n/useTranslations";

const RECENTLY_VIEWED_LOCATION = "recently-viewed-products";

const emblaOptions: EmblaOptionsType = {
  loop: false,
  align: "center",
  containScroll: "trimSnaps",
  slidesToScroll: 1,
};

export default function RecentlyViewedProductCardListClient({
  id = "",
  title,
  cardFormatSubtype = FormatSubType.HorizontallyStacked,
  cardWrapperClassName = "",
  contractText = "",
  className = "",
  container_class,
  expandText = "",
  initialCardsLength = 4,
  isProfEdPage = false,
  location: locationProp = "",
  products,
  showAllProducts = false,
  maxRows = 2,
  tagularPageType = "Homepage",
}: RecentlyViewedProductCardListClientProps) {
  const tCommon = useTranslations("Common");
  const resolvedTitle = title ?? tCommon("recentlyViewed");
  const carouselMessages = useCarouselMessages();

  const [isExpanded, setIsExpanded] = useState(false);
  const locale = useLocale();
  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions);
  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  const expandLabel =
    expandText || (locale === "es" ? "Ver más" : "Show more recent activity");
  const contractLabel =
    contractText ||
    (locale === "es" ? "Ver menos" : "Show less recent activity");

  const effectiveLocation = locationProp || RECENTLY_VIEWED_LOCATION;

  const mergedWebContext = useMemo(
    () => buildRecentlyViewedMergedWebContext(tagularPageType),
    [tagularPageType],
  );

  const tagular_variant = isProfEdPage
    ? TagularVariant.ProfessionalCertificate
    : undefined;

  const showMoreVisibleLabel = isExpanded ? contractLabel : expandLabel;

  const showMoreWebElement: ElementClicked["webElement"] = useMemo(
    () => ({
      location: effectiveLocation,
      position: "1",
      elementType: ElementType.Button,
      htmlId: "show-more-link",
      text: showMoreVisibleLabel,
      name: hyphenateForTagular(showMoreVisibleLabel),
    }),
    [effectiveLocation, showMoreVisibleLabel],
  );

  const moduleWebElement: ElementClicked["webElement"] = useMemo(
    () => ({
      elementType: ElementType.Component,
      htmlId: "recently-viewed-products-module",
      location: effectiveLocation,
      position: "1",
      name: "recently-viewed-products-module",
      text: "Recently viewed",
    }),
    [effectiveLocation],
  );

  const { elementClicked: showMoreClicked, elementViewed: showMoreViewed } =
    useElementEvents({
      webElement: showMoreWebElement,
      webContext: mergedWebContext,
    });

  const { elementViewed: moduleViewed } = useElementEvents({
    webElement: moduleWebElement,
    webContext: mergedWebContext,
  });

  const showMoreViewedRef = useRef(false);
  const moduleViewedRef = useRef(false);

  const { ref: showMoreRef, inView: showMoreInView } = useInView(
    DEFAULT_USE_IN_VIEW_OPTIONS,
  );

  const { ref: moduleContainerRef, inView: moduleInView } = useInView(
    DEFAULT_USE_IN_VIEW_OPTIONS,
  );

  useEffect(() => {
    if (moduleInView && !moduleViewedRef.current) {
      moduleViewedRef.current = true;
      moduleViewed();
    }
  }, [moduleInView, moduleViewed]);

  useEffect(() => {
    if (showMoreInView && !showMoreViewedRef.current) {
      showMoreViewedRef.current = true;
      showMoreViewed();
    }
  }, [showMoreInView, showMoreViewed]);

  const initialProducts = useMemo(
    () => products.slice(0, initialCardsLength),
    [products, initialCardsLength],
  );

  const COLS_PER_ROW = 4;
  const cappedProducts = useMemo(
    () =>
      showAllProducts ? products : products.slice(0, maxRows * COLS_PER_ROW),
    [products, showAllProducts, maxRows],
  );

  const titleId = makeSafeAttributeString(resolvedTitle);
  const skipEvents: typeof products = isExpanded ? initialProducts : [];
  const shouldShowCarouselControls = scrollSnaps.length > 1;
  const shouldShowMore =
    !showAllProducts && cappedProducts.length > initialCardsLength;

  const handleShowButtonClick = () => {
    showMoreClicked();
    setIsExpanded(!isExpanded);
  };

  const rtf = useMemo(
    () => new Intl.RelativeTimeFormat(locale, { numeric: "auto" }),
    [locale],
  );

  const allCards = useMemo(
    () =>
      cappedProducts.map((product, index) => {
        const cardProps = convertToRecentlyViewedCard(product);
        const minutesAgo = cardProps.viewedSince ?? 0;
        const viewedLabel = formatViewedLabel(minutesAgo, rtf);
        return { product, cardProps, viewedLabel, index };
      }),
    [cappedProducts, rtf],
  );

  const cards =
    isExpanded || showAllProducts
      ? allCards
      : allCards.slice(0, initialCardsLength);

  const buildCardTagularDimensions = useCallback(
    (cardProps: (typeof allCards)[number]["cardProps"]) => {
      return buildRecentlyViewedProductCustomDimensions({
        weeksToComplete: cardProps.weeksToComplete,
        level: cardProps.level,
        pacing: cardProps.pacing,
        attributes: cardProps.attributes,
      });
    },
    [],
  );

  function cardItem(
    { product, cardProps, viewedLabel, index }: (typeof cards)[number],
    wrapperClassName?: string,
  ) {
    return (
      <div
        key={`${effectiveLocation}-${product.productUuid}`}
        className={wrapperClassName}
      >
        <ProductCard
          {...cardProps}
          location={effectiveLocation}
          index={index}
          formatType={FormatType.ProductCard}
          formatSubtype={cardFormatSubtype}
          skipViewEvent={skipEvents?.includes(product)}
          tagular_variant={tagular_variant}
          tagularCustomDimensions={buildCardTagularDimensions(cardProps)}
          tagularWebContext={mergedWebContext}
          className="no-underline hover:no-underline md:!max-w-full"
        />
        <p className="text-md text-gray-80 mb-0 text-center md:text-left">
          Viewed {viewedLabel}
        </p>
      </div>
    );
  }

  if (products.length === 0) return null;

  return (
    <>
      <div
        ref={moduleContainerRef}
        className={cn(
          "flex flex-col max-w-[1280px] mx-auto w-full gap-8",
          cardWrapperClassName,
        )}
      >
        {resolvedTitle && (
          <h2
            id={titleId || undefined}
            className="my-0 text-primary text-center md:text-left md:text-3xl font-bold"
          >
            <span className="inline-flex gap-2 items-center justify-center break-words whitespace-normal">
              <Clock svgProps={{ width: 34, height: 34 }} />
              {resolvedTitle}
            </span>
          </h2>
        )}

        {/* Mobile: Embla carousel */}
        <div className="md:hidden flex flex-col gap-8">
          <div
            className="overflow-hidden"
            ref={emblaRef}
            role="region"
            aria-label={carouselMessages.recentlyViewedProducts}
            aria-roledescription="carousel"
          >
            <div className="flex">
              {allCards.map((card) =>
                cardItem(card, "flex flex-col flex-[0_0_100%] min-w-0"),
              )}
            </div>
          </div>

          {shouldShowCarouselControls && (
            <div className="w-full flex flex-row justify-center">
              <div className="flex flex-row justify-center w-fit items-center gap-2 bg-white rounded-full border border-putty-400">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onPrevButtonClick}
                  disabled={prevBtnDisabled}
                  aria-label={carouselMessages.previousSlide}
                  className="hover:bg-transparent"
                >
                  <ChevronLeft />
                </Button>

                <div className="flex gap-6">
                  {scrollSnaps.map((_, i) => (
                    <Button
                      key={i}
                      variant="ghost"
                      size="icon"
                      onClick={() => onDotButtonClick(i)}
                      aria-label={carouselMessages.goToSlide(i + 1)}
                      className={`h-3 w-3 rounded-full p-0 ${
                        i === selectedIndex ? "bg-brand-500" : "bg-primary-500"
                      }`}
                    >
                      <Circle
                        svgProps={{
                          width: "8",
                          height: "8",
                          className:
                            i === selectedIndex
                              ? "text-brand-500"
                              : "text-primary-500",
                        }}
                      />
                    </Button>
                  ))}
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onNextButtonClick}
                  disabled={nextBtnDisabled}
                  aria-label={carouselMessages.nextSlide}
                  className="hover:bg-transparent"
                >
                  <ChevronRight />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Desktop: dynamic grid */}
        <div
          className={cn(
            "hidden md:grid",
            container_class || `dynamic-grid flex-wrap gap-4 ${className}`,
          )}
        >
          {cards.map((card) => cardItem(card, "flex flex-col gap-1"))}
        </div>

        {shouldShowMore && (
          <div className="hidden md:flex justify-center">
            <span
              role="button"
              ref={showMoreRef}
              className="cursor-pointer underline text-base hover:no-underline hover:font-bold"
              onClick={handleShowButtonClick}
            >
              {showMoreVisibleLabel}
            </span>
          </div>
        )}
      </div>
    </>
  );
}
